import type { Partner, PartnerCategory } from '../types/partners'
import { GET_STARTED_URL } from './dgpt'

/**
 * PARTNER MARKETPLACE BACKEND — REQUIRED BEFORE VOTING / APPLICATIONS WORK
 *
 * PARTNER_API_BASE: base URL of the votes API (shares the StartupFuel / DiligenceGPT™
 * session cookie; requests are sent with credentials). Expected contract:
 *   GET    {base}/partners/votes         → { authenticated: boolean, counts: { [slug]: number }, viewerVotes: string[] }
 *   PUT    {base}/partners/:slug/vote    → { count: number, voted: true }   (401 if signed out)
 *   DELETE {base}/partners/:slug/vote    → { count: number, voted: false }  (401 if signed out)
 * The server must enforce one vote per authenticated user per partner.
 *
 * PARTNER_APPLICATION_ENDPOINT: URL that accepts a JSON POST of the "Become a Partner" form
 * (your API, a CRM webhook, or a form service). Must respond 2xx on success.
 *
 * While either value is empty, the UI shows no vote counts and the form reports that online
 * applications are unavailable — nothing is faked or stored locally.
 */
export const PARTNER_API_BASE = ''
export const PARTNER_APPLICATION_ENDPOINT = ''

/** UTM parameters added to every outbound "Try Platform" link. */
export const PARTNER_UTM = { source: 'startupfuel', medium: 'partner_marketplace' } as const

export const PARTNER_CATEGORIES: PartnerCategory[] = [
  { id: 'ai-intelligence', label: 'AI & Intelligence' },
  { id: 'due-diligence', label: 'Due Diligence' },
  { id: 'intellectual-property', label: 'Intellectual Property' },
  { id: 'legal-compliance', label: 'Legal & Compliance' },
  { id: 'financial-analysis', label: 'Financial Analysis' },
  { id: 'data-research', label: 'Data & Research' },
  { id: 'expert-services', label: 'Expert Services' },
]

export const PARTNERSHIP_INTEREST_OPTIONS = [
  'Technology Integration',
  'Referral Partnership',
  'Co-Selling',
  'Marketplace Listing',
  'Other',
] as const

/** LeifAI-supplied files. Logo file not yet supplied — the name renders as text until it is. */
const LEIF = {
  logo: '',
  cover: 'https://cdn.magicpatterns.com/uploads/3LBxn8TMmyRexEGYp3L9iL/leif-report-cover-landscape.svg',
  landscape: 'https://cdn.magicpatterns.com/uploads/jH3b5tr6ppWx6RTXNaZDZn/leif-example-landscape-report.svg',
  fto: 'https://cdn.magicpatterns.com/uploads/pz2Cjat8UjoFqfVsgYJYLv/leif-example-freedom-to-operate-report.svg',
  validity: 'https://cdn.magicpatterns.com/uploads/aAssZvzQnuDWg3wSbGfm2B/leif-example-validity-report.svg',
  infringers: 'https://cdn.magicpatterns.com/uploads/et7piUgX7Hsrt8vfgLd3Us/leif-example-infringers-report.svg',
  patentability: 'https://cdn.magicpatterns.com/uploads/9oBpcHBAyuF6mRk2adxuge/leif-example-patentability-report.svg',
  onePager: 'https://cdn.magicpatterns.com/uploads/7P7Ckero2HV1Wb2r7ycxdp/LeifAI_One_Pager.pdf',
  overview: 'https://cdn.magicpatterns.com/uploads/mawHos9n2oLLYp4YxoQd5v/Leif_AI_Overview.pdf',
}

export const PARTNERS: Partner[] = [
  {
    id: 'leifai',
    slug: 'leifai',
    name: 'LeifAI',
    tagline: 'Agentic AI for Patents and Tech',
    shortDescription:
      'LeifAI helps companies, investors, and patent professionals understand intellectual property risks and opportunities through specialized AI-powered patent analysis, landscape intelligence, and proprietary scoring.',
    fullDescription:
      'LeifAI is domain-specific, agentic AI for patents and technology. It reviews relevant patents, filings, documents and webpages and returns patent landscape, freedom-to-operate, validity, infringer search and patentability reports, alongside its proprietary LCS, LVS and LPS scores.',
    logo: { src: LEIF.logo, alt: 'LeifAI', width: 480, height: 120 },
    categories: ['intellectual-property', 'ai-intelligence', 'due-diligence'],
    websiteUrl: 'https://leif-ai.com/',
    platformUrl: GET_STARTED_URL,
    tryLabel: 'Try LeifAI',
    featured: true,
    published: true,
    addedAt: '2026-10-27',
    brand: { accent: '#00009B', accentStrong: '#000070', highlight: '#4F46E5', soft: '#EEEEFB' },
    benefits: [
      'Five report types — defense and offense',
      'Proprietary LCS©, LVS© and LPS© scores',
      'No system integration, setup or specialized training',
    ],
    seo: {
      title: 'LeifAI: Agentic AI for Patent Intelligence | StartupFuel Partners',
      description:
        'LeifAI provides AI patent landscape, freedom-to-operate, validity, infringer search and patentability reports with proprietary scoring. Download sample reports and the LeifAI one-pager.',
      keywords: ['AI patent analysis', 'patent landscape report', 'freedom to operate analysis', 'patent validity', 'IP due diligence'],
    },
    profile: {
      hero: {
        eyebrow: 'StartupFuel Technology Partner',
        headline: 'Understand the Patents Behind the Business.',
        subheadline:
          'Agentic AI for patent intelligence, helping investors, startups, and innovation teams evaluate intellectual property risks, identify opportunities, and make more informed decisions.',
        statement: 'Know the most about your company’s most important assets.',
        featuredVisual: { src: LEIF.cover, alt: 'Cover of a LeifAI landscape report listing its seven sections, from Executive Summary to Market Map', width: 536, height: 798 },
      },
      context: {
        eyebrow: 'Why IP intelligence matters',
        headline: 'Before You Invest in the Company, Understand Its IP.',
        body: [
          'For technology businesses, patents and proprietary technology are often among the most important assets — and among the hardest to assess. LeifAI notes that around 80% of S&P 500 value is intangible, yet traditional patent work is expensive, incomplete and slow, and the analysis is often not done or not communicated.',
          'General-purpose AI is too broad for this work. LeifAI is a specialized platform that reviews relevant patents, filings, documents and webpages to help users understand patent landscapes, risks, validity and commercialization opportunities.',
        ],
        factsLabel: 'Technology, as described by LeifAI',
        facts: [
          'Precomputed coverage of the entire US patent database',
          'Built on 10+ years of strategic patent and technology services experience',
          'Accepts patent numbers, technical specifications, pitch decks and other inputs',
          'No system integration, setup or specialized training required',
        ],
        audiences: [
          { icon: 'investors', title: 'Venture Capital & Investors', body: 'Evaluate potential intellectual property risks and opportunities before making an investment decision.' },
          { icon: 'founders', title: 'Startups & Founders', body: 'Understand the patent landscape surrounding your innovation and identify potential barriers or opportunities.' },
          { icon: 'corporate', title: 'Corporate Innovation Teams', body: 'Evaluate technologies, intellectual property exposure, and potential commercialization opportunities.' },
          { icon: 'advisors', title: 'Patent Attorneys & Advisors', body: 'Use specialized AI analysis to support patent research, review, and strategic decision-making.' },
        ],
      },
      reports: {
        eyebrow: 'Sample reports',
        headline: 'Five Ways to Understand Your Intellectual Property.',
        intro:
          'Example reports supplied by LeifAI, covering defense (freedom to operate, validity, landscape) and offense (infringer search, patentability, landscape). Open any report full size or download it.',
        items: [
          {
            id: 'patent_landscape',
            title: 'Patent Landscape',
            description: 'Map relevant patents, identify technology trends, understand competitive positioning, and uncover differentiators.',
            highlights: ['Market map of patents and IP', 'Dynamic ideation of high-value features', 'Isolates differentiators and identifies trends'],
            image: { src: LEIF.landscape, alt: 'Example LeifAI landscape report: 421 patents in the landscape, 12 features mapped, with a feature-overlap chart', width: 552, height: 460 },
            downloadName: 'LeifAI-Example-Landscape-Report.svg',
          },
          {
            id: 'freedom_to_operate',
            title: 'Freedom to Operate',
            description: 'Evaluate potentially problematic patent claims and identify intellectual property clearance risks.',
            highlights: ['Identifies problematic patent claims', 'Precise element-by-element comparison', 'Proprietary Leif Clearance Score (LCS©)'],
            image: { src: LEIF.fto, alt: 'Example LeifAI freedom-to-operate report rated medium risk: 4 of 5 in-force patents come close to reading on the product', width: 552, height: 518 },
            downloadName: 'LeifAI-Example-Freedom-to-Operate-Report.svg',
          },
          {
            id: 'patent_validity',
            title: 'Patent Validity',
            description: 'Assess patent validity through prior-art research and novelty and obviousness analysis.',
            highlights: ['Covers prior art worldwide', 'Stringent novelty and obviousness analysis', 'Proprietary Leif Validity Score (LVS©)'],
            image: { src: LEIF.validity, alt: 'Example LeifAI validity report with a Leif Validity Score of 8, indicating low likelihood the claim survives a validity challenge', width: 553, height: 528 },
            downloadName: 'LeifAI-Example-Validity-Report.svg',
          },
          {
            id: 'infringer_search',
            title: 'Infringer Search',
            description: 'Identify potential infringement activity through patent claim analysis and technology comparisons.',
            highlights: ['Covers infringement activity across products', 'Element-by-element claim construction', 'Exhaustive infringement analysis'],
            image: { src: LEIF.infringers, alt: 'Example LeifAI infringers report: 1 of 12 products fully covered by an independent claim', width: 552, height: 500 },
            downloadName: 'LeifAI-Example-Infringers-Report.svg',
          },
          {
            id: 'patentability',
            title: 'Patentability',
            description: 'Explore whether an invention may qualify for patent protection through prior-art comparisons and patentability analysis.',
            highlights: ['Element comparison to prior art worldwide', 'Calibrated to real PTAB decisions', 'Proprietary Leif Patentability Score (LPS©)'],
            image: { src: LEIF.patentability, alt: 'Example LeifAI patentability report rated moderate likelihood: 3 of 16 features likely patentable', width: 552, height: 460 },
            downloadName: 'LeifAI-Example-Patentability-Report.svg',
          },
        ],
      },
      resources: {
        eyebrow: 'Downloads',
        headline: 'Take LeifAI back to your team.',
        intro: 'LeifAI’s one-pager and company overview, as supplied by LeifAI.',
        documents: [
          {
            id: 'one_pager',
            title: 'LeifAI One-Pager',
            description: 'The problem, the five reports, proprietary scores, target customers and technology on a single page.',
            url: LEIF.onePager,
            fileName: 'LeifAI-One-Pager.pdf',
            format: 'PDF · 1 page',
          },
          {
            id: 'company_overview',
            title: 'LeifAI Company Overview',
            description: 'Executive summary, product overview, team, market overview and report details.',
            url: LEIF.overview,
            fileName: 'LeifAI-Company-Overview.pdf',
            format: 'PDF · 10 pages',
          },
        ],
        teamNote: 'LeifAI is led by CEO Eric Morehouse — a patent attorney for 30+ years and Founder/CEO of AiPi — with a six-person team based in Tysons Corner, Virginia.',
      },
      scores: {
        eyebrow: 'Proprietary LeifAI scoring',
        headline: 'Go Beyond Documents. Understand the Scores.',
        intro: 'Alongside its reports, LeifAI applies three proprietary scoring methodologies. These scores are produced by LeifAI, not by StartupFuel or DiligenceGPT™.',
        items: [
          { code: 'LCS©', name: 'Leif Clearance Score', description: 'Supports the assessment of freedom-to-operate and patent clearance risks.' },
          { code: 'LVS©', name: 'Leif Validity Score', description: 'Supports the assessment of patent validity and prior-art considerations.' },
          { code: 'LPS©', name: 'Leif Patentability Score', description: 'Supports the assessment of patentability based on relevant technical and patent information.' },
        ],
        disclaimer:
          'LeifAI reports and scores are AI-generated analysis provided for informational purposes. They are not legal advice and do not replace review by a qualified patent attorney or legal professional.',
      },
      howItWorks: {
        eyebrow: 'How it works',
        headline: 'From technology to intelligence in three steps.',
        steps: [
          { title: 'Provide Your Technology', body: 'Submit relevant patent numbers, technical specifications, pitch decks, or supporting documents through LeifAI.' },
          { title: 'Generate Intelligence', body: 'LeifAI analyzes relevant patent information and produces specialized intelligence reports.' },
          { title: 'Review the Findings', body: 'Explore patent risks, competitive landscapes, opportunities, and proprietary scores.' },
        ],
        note: 'LeifAI states that each report is delivered in under 30 minutes. Timing is a LeifAI-stated capability, not a StartupFuel guarantee.',
      },
      finalCta: {
        headline: 'See what LeifAI finds in your technology.',
        body: 'Get started to explore LeifAI, and upvote it to tell us which partner capabilities you want to see more of across StartupFuel.',
      },
      relationshipNote:
        'LeifAI is an independent company listed in the StartupFuel partner marketplace. It is not currently integrated into DiligenceGPT™, and LeifAI reports are produced and delivered by LeifAI.',
    },
  },
]

export const PUBLISHED_PARTNERS = PARTNERS.filter((partner) => partner.published)

/** Only categories that at least one published partner belongs to — empty filters stay hidden. */
export const ACTIVE_PARTNER_CATEGORIES = PARTNER_CATEGORIES.filter((category) =>
  PUBLISHED_PARTNERS.some((partner) => partner.categories.includes(category.id)),
)

export function getPartner(slug: string | undefined) {
  return PUBLISHED_PARTNERS.find((partner) => partner.slug === slug)
}

export function categoryLabel(id: string) {
  return PARTNER_CATEGORIES.find((category) => category.id === id)?.label ?? id
}
